// src/components/common/OrientationGuard.js
import React, { useState, useEffect } from 'react';

/**
 * Trava de Segurança para Orientação Vertical (Portrait)
 * 1. Pede bloqueio de orientação no Screen Orientation API para 'portrait' se disponível (PWA/Android).
 * 2. Em telas móveis e tablets (largura de tela <= 1024px), se o usuário deitar o aparelho (modo paisagem/horizontal),
 *    bloqueia a interface com uma sobreposição amigável solicitando colocar o dispositivo em pé (vertical).
 * 3. Permite o uso normal em notebooks/desktops widescreen (largura > 1024px e mouse pointer).
 */
export default function OrientationGuard() {
    const [isLandscapeMobile, setIsLandscapeMobile] = useState(false);

    useEffect(() => {
        // Tenta travar nativamente no PWA via Screen Orientation API
        const tryLockOrientation = async () => {
            try {
                if (window.screen && window.screen.orientation && window.screen.orientation.lock) {
                    await window.screen.orientation.lock('portrait').catch(() => {});
                }
            } catch (e) {
                // Silencioso se não suportado pelo navegador
            }
        };

        tryLockOrientation();

        const checkOrientation = () => {
            const width = window.innerWidth;
            const height = window.innerHeight;

            // Detecta se é um tablet ou celular virado de lado (horizontal):
            // 1. Largura maior que altura
            // 2. Altura máxima condizente com celulares/tablets na horizontal (<= 1024px)
            // 3. Suporte a touch ou User-Agent móvel/tablet
            const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
            const isHorizontal = width > height;
            const isMobileDimension = height <= 1024 && width <= 1366;

            const shouldBlock = isHorizontal && isTouchDevice && isMobileDimension;
            setIsLandscapeMobile(shouldBlock);
        };

        checkOrientation();

        window.addEventListener('resize', checkOrientation);
        window.addEventListener('orientationchange', checkOrientation);

        if (window.screen && window.screen.orientation) {
            window.screen.orientation.addEventListener('change', checkOrientation);
        }

        return () => {
            window.removeEventListener('resize', checkOrientation);
            window.removeEventListener('orientationchange', checkOrientation);
            if (window.screen && window.screen.orientation) {
                window.screen.orientation.removeEventListener('change', checkOrientation);
            }
        };
    }, []);

    if (!isLandscapeMobile) return null;

    return (
        <div className="fixed inset-0 z-[999999] bg-slate-950/95 backdrop-blur-xl flex flex-col items-center justify-center p-6 text-center text-white select-none animate-fadeIn">
            {/* Ícone Animado de Rotação para a Vertical */}
            <div className="relative mb-6">
                {/* Aparelho na horizontal */}
                <div className="w-32 h-20 border-4 border-slate-700 rounded-2xl flex items-center justify-center p-2 shadow-2xl relative animate-pulse">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-600 absolute left-2"></div>
                    <div className="w-1 h-8 bg-slate-700 rounded-full absolute right-2"></div>
                </div>
                {/* Seta circular animada indicando rotação para em pé */}
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-28 h-28 border-2 border-cyan-400 border-t-transparent border-dashed rounded-full animate-spin" style={{ animationDuration: '3s' }}></div>
                </div>
                <div className="absolute -bottom-3 -right-3 bg-cyan-500 text-slate-950 p-2 rounded-full shadow-lg">
                    <svg className="w-5 h-5 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 19V5m-7 7l7-7 7 7" />
                    </svg>
                </div>
            </div>

            <div className="max-w-sm space-y-2">
                <span className="inline-block px-3 py-1 bg-cyan-950/80 border border-cyan-800/60 text-cyan-400 text-[10px] font-black uppercase tracking-widest rounded-full">
                    Orientação Obrigatória
                </span>
                <h3 className="text-xl font-black text-white uppercase tracking-tight">
                    Gire o aparelho para a Vertical
                </h3>
                <p className="text-xs text-slate-400 leading-relaxed">
                    O aplicativo e o Portal Arcus foram configurados para operar exclusivamente no modo <strong className="text-cyan-300">Vertical (Em pé)</strong> em tablets e celulares.
                </p>
            </div>

            {/* Dica para o usuário */}
            <div className="mt-8 text-[11px] text-slate-500 font-mono flex items-center gap-1.5">
                <span>📱</span> Coloque o aparelho na vertical para continuar navegando.
            </div>
        </div>
    );
}
